import type { MouseEventHandler, PointerEventHandler } from "react"
import { useCallback, useRef } from "react"

const DEFAULT_MAX_DELAY_MS = 300
const DEFAULT_MAX_DISTANCE_PX = 8
const SYNTHETIC_DOUBLE_CLICK_SUPPRESSION_MS = 500
const DEFAULT_EXCLUDED_TARGETS =
  "a, button, input, textarea, select, [role=button]"

type TouchGesture = {
  pointerId: number
  startedAt: number
  startX: number
  startY: number
  moved: boolean
}

type DoubleActivateProps<Element extends HTMLElement> = {
  onDoubleClick?: MouseEventHandler<Element>
  onPointerCancel?: PointerEventHandler<Element>
  onPointerDown?: PointerEventHandler<Element>
  onPointerMove?: PointerEventHandler<Element>
  onPointerUp?: PointerEventHandler<Element>
}

type UseDoubleActivateOptions<Element extends HTMLElement> = {
  onDoubleActivate?: () => void
  /** Additional descendants that should retain their own interactions. */
  excludeTargetSelector?: string
  maxDelayMs?: number
  maxDistancePx?: number
  /** Composes another eligible-surface gesture, such as row dragging. */
  onEligiblePointerDown?: PointerEventHandler<Element>
}

/**
 * Adds double click/tap to a component
 * Interactive descendants are excluded, and touch movement cancels activation
 * so a drag gesture always wins.
 */
const useDoubleActivate = <Element extends HTMLElement>({
  onDoubleActivate,
  excludeTargetSelector,
  maxDelayMs = DEFAULT_MAX_DELAY_MS,
  maxDistancePx = DEFAULT_MAX_DISTANCE_PX,
  onEligiblePointerDown
}: UseDoubleActivateOptions<Element>): DoubleActivateProps<Element> => {
  const touchGestureRef = useRef<TouchGesture | null>(null)
  const lastTouchTapRef = useRef<number | null>(null)
  const suppressDoubleClickUntilRef = useRef(0)

  const isEligibleTarget = useCallback(
    (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false

      const excludedTargets = excludeTargetSelector
        ? `${DEFAULT_EXCLUDED_TARGETS}, ${excludeTargetSelector}`
        : DEFAULT_EXCLUDED_TARGETS
      return !target.closest(excludedTargets)
    },
    [excludeTargetSelector]
  )

  const handlePointerDown = useCallback<PointerEventHandler<Element>>(
    (event) => {
      if (!isEligibleTarget(event.target)) return

      if (onDoubleActivate && event.pointerType !== "mouse") {
        touchGestureRef.current = {
          pointerId: event.pointerId,
          startedAt: event.timeStamp,
          startX: event.clientX,
          startY: event.clientY,
          moved: false
        }
      }

      onEligiblePointerDown?.(event)
    },
    [isEligibleTarget, onDoubleActivate, onEligiblePointerDown]
  )

  const handlePointerMove = useCallback<PointerEventHandler<Element>>(
    (event) => {
      const gesture = touchGestureRef.current
      if (!gesture || gesture.pointerId !== event.pointerId || gesture.moved) {
        return
      }

      const distance = Math.hypot(
        event.clientX - gesture.startX,
        event.clientY - gesture.startY
      )
      if (distance > maxDistancePx) gesture.moved = true
    },
    [maxDistancePx]
  )

  const handlePointerUp = useCallback<PointerEventHandler<Element>>(
    (event) => {
      const gesture = touchGestureRef.current
      touchGestureRef.current = null
      const moved =
        gesture?.moved ||
        (gesture
          ? Math.hypot(
              event.clientX - gesture.startX,
              event.clientY - gesture.startY
            ) > maxDistancePx
          : false)

      if (
        !onDoubleActivate ||
        !gesture ||
        gesture.pointerId !== event.pointerId ||
        moved ||
        event.timeStamp - gesture.startedAt > maxDelayMs
      ) {
        return
      }

      const lastTap = lastTouchTapRef.current
      if (lastTap !== null && event.timeStamp - lastTap <= maxDelayMs) {
        lastTouchTapRef.current = null
        suppressDoubleClickUntilRef.current =
          Date.now() + SYNTHETIC_DOUBLE_CLICK_SUPPRESSION_MS
        onDoubleActivate()
        return
      }

      lastTouchTapRef.current = event.timeStamp
    },
    [maxDelayMs, maxDistancePx, onDoubleActivate]
  )

  const handlePointerCancel = useCallback<PointerEventHandler<Element>>(() => {
    touchGestureRef.current = null
    lastTouchTapRef.current = null
  }, [])

  const handleDoubleClick = useCallback<MouseEventHandler<Element>>(
    (event) => {
      if (
        onDoubleActivate &&
        Date.now() >= suppressDoubleClickUntilRef.current &&
        isEligibleTarget(event.target)
      ) {
        onDoubleActivate()
      }
    },
    [isEligibleTarget, onDoubleActivate]
  )

  return {
    onDoubleClick: onDoubleActivate ? handleDoubleClick : undefined,
    onPointerCancel: onDoubleActivate ? handlePointerCancel : undefined,
    onPointerDown:
      onDoubleActivate || onEligiblePointerDown ? handlePointerDown : undefined,
    onPointerMove: onDoubleActivate ? handlePointerMove : undefined,
    onPointerUp: onDoubleActivate ? handlePointerUp : undefined
  }
}

export default useDoubleActivate
